import { z } from 'zod';

export const MewsEventSchema = z.object({
  event: z.unknown(),
  data: z.object({
    id: z.string(),
    arrival_date: z.coerce.date(),
    departure_date: z.coerce.date(),
  }),
});

export type MewsEvent = z.infer<typeof MewsEventSchema>;

/**
 * Mews API credentials for authentication
 */
export const MewsCredentialSchema = z.object({
  /** Access token provided by Mews */
  accessToken: z.string().min(1),
});
export type MewsCredential = z.infer<typeof MewsCredentialSchema>;

/** Mews pricing environment */
export enum MewsPricingEnvironment {
  Gross = 'Gross',
  Net = 'Net',
}

/**
 * Configuration options for Mews integration
 */
export const MewsConfigSchema = z.object({
  /** Default timeout for API calls in milliseconds */
  apiTimeout: z.number().optional(),
  /** Whether to automatically delete Way bookings when reservation is cancelled */
  deleteWayBookingWithReservationCancel: z.boolean().optional(),
  /** Whether to enable pre-arrival charges */
  enablePreArrivalCharges: z.boolean().optional(),
  /** Whether to validate complimentary bookings */
  shouldValidateComplimentaryBooking: z.boolean().optional(),
  pricingEnvironment: z.enum([MewsPricingEnvironment.Gross, MewsPricingEnvironment.Net]).optional(),
});
export type MewsConfig = z.infer<typeof MewsConfigSchema>;

export const MewsConfigResponseSchema = z.object({
  data: MewsConfigSchema,
});
export type MewsConfigResponse = z.infer<typeof MewsConfigResponseSchema>;

export enum MewsFormFieldKeys {
  AccountingCategoryId = 'accountingCategoryId',
  ServiceId = 'serviceId',
  TaxCodes = 'taxCodes',
}

export const MewsIntegrationMetaSchema = z.object({
  serviceId: z.string().min(1, { message: 'Service is required' }),
  accountingCategoryId: z.string().min(1, { message: 'Accounting category is required' }),
  taxCodes: z.array(z.string()).optional(),
});

export const GetMewsServicesResponseSchema = z.array(
  z.object({
    code: z.string(),
    name: z.string(),
    description: z.boolean(),
  }),
);

export type GetMewsServicesResponse = z.infer<typeof GetMewsServicesResponseSchema>;

export const GetMewsAccountCategoriesResponseSchema = z.array(
  z.object({
    code: z.string(),
    name: z.string(),
    description: z.boolean(),
  }),
);

export type GetMewsAccountCategoriesResponse = z.infer<typeof GetMewsAccountCategoriesResponseSchema>;

export const GetMewsTaxCodesResponseSchema = z.array(
  z.object({
    code: z.string(),
    name: z.string(),
  }),
);

export type GetMewsTaxCodesResponse = z.infer<typeof GetMewsTaxCodesResponseSchema>;
